import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
	static targets = [
		"indicator",
		"option",
		"heroSummary",
		"heroBadge",
		"heroFootnote",
		"card",
		"price",
		"priceSuffix",
		"equivalent",
		"savings",
		"cta",
	];

	static values = {
		active: String,
	};

	connect() {
		this.prefersReducedMotion = window.matchMedia(
			"(prefers-reduced-motion: reduce)",
		).matches;
		this.timeouts = new Map();
		this.frames = new Map();
		this.isInitialRender = true;
		this.update();
		this.isInitialRender = false;
	}

	disconnect() {
		this.cancelAllAnimations();
	}

	select(event) {
		this.activeValue = event.currentTarget.dataset.duration;
		this.update();
	}

	update() {
		const duration = this.activeValue || "monthly";
		const key = this.keyFor(duration);

		this.optionTargets.forEach((option) => {
			const isActive = option.dataset.duration === duration;

			option.setAttribute("aria-pressed", String(isActive));
			option.dataset.active = String(isActive);
		});

		this.syncIndicator(duration);

		this.heroSummaryTargets.forEach((target) => {
			this.animateTextSwap(target, target.dataset[`summary${key}`]);
		});

		this.heroBadgeTargets.forEach((target) => {
			this.animateTextSwap(target, target.dataset[`badge${key}`], 45);
		});

		this.heroFootnoteTargets.forEach((target) => {
			this.animateTextSwap(target, target.dataset[`footnote${key}`], 90);
		});

		this.cardTargets.forEach((card, index) => {
			const priceTarget = this.priceTargets.find(
				(target) => target.dataset.plan === card.dataset.plan,
			);
			const suffixTarget = this.priceSuffixTargets.find(
				(target) => target.dataset.plan === card.dataset.plan,
			);
			const equivalentTarget = this.equivalentTargets.find(
				(target) => target.dataset.plan === card.dataset.plan,
			);
			const savingsTarget = this.savingsTargets.find(
				(target) => target.dataset.plan === card.dataset.plan,
			);
			const ctaTarget = this.ctaTargets.find(
				(target) => target.dataset.plan === card.dataset.plan,
			);
			const stagger = index * 55;

			this.animateCard(card, stagger);

			if (priceTarget) {
				this.animatePriceValue(
					priceTarget,
					card.dataset[`price${key}`],
					stagger,
				);
			}

			if (suffixTarget) {
				this.animateTextSwap(
					suffixTarget,
					card.dataset[`suffix${key}`],
					stagger,
				);
			}

			if (equivalentTarget) {
				this.animateTextSwap(
					equivalentTarget,
					card.dataset[`equivalent${key}`],
					stagger + 35,
				);
			}

			if (savingsTarget) {
				this.animateTextSwap(
					savingsTarget,
					card.dataset[`savings${key}`],
					stagger + 65,
				);
			}

			if (ctaTarget) {
				this.animateTextSwap(
					ctaTarget,
					card.dataset[`cta${key}`],
					stagger + 85,
				);
			}
		});
	}

	syncIndicator(duration) {
		if (!this.hasIndicatorTarget) {
			return;
		}

		const activeOption = this.optionTargets.find(
			(option) => option.dataset.duration === duration,
		);

		if (!activeOption) {
			return;
		}

		this.indicatorTarget.style.width = `${activeOption.offsetWidth}px`;
		this.indicatorTarget.style.height = `${activeOption.offsetHeight}px`;
		this.indicatorTarget.style.transform = `translate(${activeOption.offsetLeft}px, ${activeOption.offsetTop}px)`;
		this.indicatorTarget.style.opacity = "1";

		if (this.isInitialRender || this.prefersReducedMotion) {
			this.indicatorTarget.style.transitionDuration = "0ms";

			window.requestAnimationFrame(() => {
				this.indicatorTarget.style.removeProperty("transition-duration");
			});
		}
	}

	animateCard(card, delay = 0) {
		card.classList.remove("is-animating");
		void card.offsetWidth;
		card.classList.add("is-animating");

		window.setTimeout(() => {
			card.classList.remove("is-animating");
		}, 720 + delay);

		if (this.prefersReducedMotion || this.isInitialRender) {
			return;
		}

		const restingY = card.classList.contains("featured") ? -12 : 0;

		card.animate(
			[
				{
					filter: "brightness(1)",
					transform: `translateY(${restingY}px) scale(1)`,
				},
				{
					filter: "brightness(1.12)",
					transform: `translateY(${restingY - 8}px) scale(0.985)`,
				},
				{
					filter: "brightness(1)",
					transform: `translateY(${restingY}px) scale(1)`,
				},
			],
			{
				delay,
				duration: 620,
				easing: "cubic-bezier(0.22, 1, 0.36, 1)",
			},
		);
	}

	animateTextSwap(target, nextText, delay = 0) {
		if (!nextText || target.textContent === nextText) {
			return;
		}

		this.cancelAnimation(target);

		if (this.prefersReducedMotion || this.isInitialRender) {
			target.textContent = nextText;
			return;
		}

		this.schedule(target, delay, () => {
			target.animate(
				[
					{ filter: "blur(0px)", opacity: 1, transform: "translateY(0px)" },
					{
						filter: "blur(10px)",
						opacity: 0.08,
						transform: "translateY(-10px)",
					},
				],
				{
					duration: 160,
					easing: "ease-in",
					fill: "forwards",
				},
			);

			this.schedule(target, 140, () => {
				target.textContent = nextText;

				target.animate(
					[
						{
							filter: "blur(10px)",
							opacity: 0.08,
							transform: "translateY(12px)",
						},
						{ filter: "blur(0px)", opacity: 1, transform: "translateY(0px)" },
					],
					{
						duration: 320,
						easing: "cubic-bezier(0.22, 1, 0.36, 1)",
						fill: "both",
					},
				);
			});
		});
	}

	animatePriceValue(target, nextText, delay = 0) {
		if (!nextText || target.textContent === nextText) {
			return;
		}

		const currentValue = this.parseCurrency(target.textContent);
		const nextValue = this.parseCurrency(nextText);

		if (
			currentValue === null ||
			nextValue === null ||
			this.prefersReducedMotion ||
			this.isInitialRender
		) {
			target.textContent = nextText;
			return;
		}

		this.cancelAnimation(target);

		this.schedule(target, delay, () => {
			target.animate(
				[
					{
						filter: "blur(0px)",
						opacity: 1,
						transform: "translateY(0px) scale(1)",
					},
					{
						filter: "blur(8px)",
						opacity: 0.28,
						transform: "translateY(12px) scale(0.94)",
					},
				],
				{
					duration: 140,
					easing: "ease-in",
					fill: "forwards",
				},
			);

			this.schedule(target, 110, () => {
				const startedAt = performance.now();
				const durationMs = 460;

				target.animate(
					[
						{
							filter: "blur(8px)",
							opacity: 0.22,
							transform: "translateY(-10px) scale(0.94)",
						},
						{
							filter: "blur(0px)",
							opacity: 1,
							transform: "translateY(0px) scale(1)",
						},
					],
					{
						duration: durationMs,
						easing: "cubic-bezier(0.22, 1, 0.36, 1)",
						fill: "both",
					},
				);

				const tick = (now) => {
					const progress = Math.min((now - startedAt) / durationMs, 1);
					const eased = 1 - (1 - progress) ** 3;
					const value = Math.round(
						currentValue + (nextValue - currentValue) * eased,
					);

					target.textContent = this.formatCurrency(value);

					if (progress < 1) {
						this.frames.set(target, window.requestAnimationFrame(tick));
						return;
					}

					target.textContent = nextText;
					this.frames.delete(target);
				};

				this.frames.set(target, window.requestAnimationFrame(tick));
			});
		});
	}

	schedule(target, delay, callback) {
		this.cancelTimer(target);

		const timeoutId = window.setTimeout(() => {
			this.timeouts.delete(target);
			callback();
		}, delay);

		this.timeouts.set(target, timeoutId);
	}

	cancelAllAnimations() {
		this.timeouts.forEach((timeoutId) => {
			window.clearTimeout(timeoutId);
		});
		this.timeouts.clear();

		this.frames.forEach((frameId) => {
			window.cancelAnimationFrame(frameId);
		});
		this.frames.clear();
	}

	cancelAnimation(target) {
		this.cancelTimer(target);

		const frameId = this.frames.get(target);
		if (frameId) {
			window.cancelAnimationFrame(frameId);
			this.frames.delete(target);
		}

		target.getAnimations().forEach((animation) => {
			animation.cancel();
		});
	}

	cancelTimer(target) {
		const timeoutId = this.timeouts.get(target);
		if (timeoutId) {
			window.clearTimeout(timeoutId);
			this.timeouts.delete(target);
		}
	}

	parseCurrency(value) {
		const digits = value.replace(/[^0-9]/g, "");
		return digits ? Number.parseInt(digits, 10) : null;
	}

	formatCurrency(value) {
		return new Intl.NumberFormat("en-US", {
			currency: "USD",
			maximumFractionDigits: 0,
			style: "currency",
		}).format(value);
	}

	keyFor(duration) {
		switch (duration) {
			case "two_months":
				return "TwoMonths";
			case "six_months":
				return "SixMonths";
			case "yearly":
				return "Yearly";
			default:
				return "Monthly";
		}
	}
}
